import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faPhone,
  faEnvelope,
  faBox,
  faTruck,
  faHeadset,
  faSeedling,
  faInfoCircle,
  faHome,
  faClock,
  faShieldAlt,
  faCertificate,
  faCheckCircle,
  faWhatsapp as faWhatsappSolid,
} from "@fortawesome/free-solid-svg-icons";
import {
  faWhatsapp,
  faFacebook,
  faInstagram,
  faTwitter,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import logoImage from "../../assets/Logo.png";
import "./footer.scss";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// QUICK LINKS
// ============================================
const QUICK_LINKS = [
  { to: "/", label: "Home", icon: faHome },
  { to: "/about", label: "About Us", icon: faInfoCircle },
  { to: "/products", label: "Products", icon: faBox },
  { to: "/preorder", label: "Pre-Order", icon: faClock },
  { to: "/track-order", label: "Track Order", icon: faTruck },
  { to: "/testimonials", label: "Testimonials", icon: faHeadset },
  { to: "/contact", label: "Contact", icon: faEnvelope },
];

// ============================================
// SOCIAL LINKS
// ============================================
const SOCIAL_LINKS = [
  { icon: faWhatsapp, url: "https://wa.me/23231030808", label: "WhatsApp", color: "#25d366" },
  { icon: faFacebook, url: "https://facebook.com/kenalienterprisesl", label: "Facebook", color: "#1877f2" },
  { icon: faInstagram, url: "https://instagram.com/kenalienterprisesl", label: "Instagram", color: "#e4405f" },
  { icon: faTwitter, url: "https://twitter.com/kenalienterprise", label: "Twitter", color: "#1da1f2" },
  { icon: faYoutube, url: "https://youtube.com/kenalienterprise", label: "YouTube", color: "#ff0000" },
];

// ============================================
// PAYMENT METHODS
// ============================================
const PAYMENT_METHODS = [
  { label: "Orange Money", icon: "📱" },
  { label: "Afrimoney", icon: "📱" },
  { label: "WAVE", icon: "🌊" },
  { label: "Cash on Delivery", icon: "💵" },
];

// ============================================
// MAIN FOOTER COMPONENT
// ============================================
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer">
      <div className="footer-container">
        {/* ============================================
            BRAND SECTION
            ============================================ */}
        <motion.div
          className="footer-section brand-section"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="footer-logo">
            <img
              src={logoImage}
              alt="Kenali Enterprise Logo"
              className="footer-logo-img"
            />
            <h3>
              <FontAwesomeIcon icon={faSeedling} />
              Kenali Enterprise
            </h3>
          </div>
          <p className="footer-description">
            Premium livestock nutrition solutions for healthier animals and
            improved farm productivity. Trusted quality from Malaysia to Sierra
            Leone.
          </p>
          <div className="footer-tagline">🌾 Nutrition • Growth • Prosperity</div>

          {/* Trust Badges */}
          <div className="footer-trust-badges">
            <span className="trust-badge">
              <FontAwesomeIcon icon={faCertificate} />
              NRA Registered
            </span>
            <span className="trust-badge">
              <FontAwesomeIcon icon={faShieldAlt} />
              Quality Guaranteed
            </span>
            <span className="trust-badge">
              <FontAwesomeIcon icon={faCheckCircle} />
              COA Available
            </span>
          </div>

          {/* Social Links */}
          <div className="footer-social">
            {SOCIAL_LINKS.map((social, index) => (
              <motion.a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="social-link"
                whileHover={{ scale: 1.15, y: -3 }}
                transition={{ duration: 0.2 }}
                style={{ "--hover-color": social.color }}
              >
                <FontAwesomeIcon icon={social.icon} />
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* ============================================
            QUICK LINKS
            ============================================ */}
        <motion.div
          className="footer-section"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <h4>Quick Links</h4>
          <ul>
            {QUICK_LINKS.map((link) => (
              <li key={link.to}>
                <Link to={link.to}>
                  <FontAwesomeIcon icon={link.icon} /> {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* ============================================
            CONTACT SECTION
            ============================================ */}
        <motion.div
          className="footer-section contact-section"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h4>Contact Us</h4>
          <ul className="contact-list">
            <li>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <span>30 Kissy Road, Freetown, Sierra Leone</span>
            </li>
            <li>
              <FontAwesomeIcon icon={faPhone} />
              <a href="tel:+23231030808" className="contact-link">
                +232 31 030808
              </a>
            </li>
            <li>
              <FontAwesomeIcon icon={faPhone} style={{ opacity: 0.7 }} />
              <a href="tel:+19492678678" className="contact-link">
                +1 949 267 8678
              </a>
            </li>
            <li>
              <FontAwesomeIcon icon={faEnvelope} />
              <a
                href="mailto:kenalienterprisesl@gmail.com"
                className="email-link"
              >
                kenalienterprisesl@gmail.com
              </a>
            </li>
            <li className="whatsapp-contact">
              <FontAwesomeIcon icon={faWhatsapp} />
              <a
                href="https://wa.me/23231030808"
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-link"
              >
                <FontAwesomeIcon icon={faWhatsapp} />
                WhatsApp Us
              </a>
            </li>
          </ul>

          {/* Working Hours */}
          <div className="working-hours">
            <FontAwesomeIcon icon={faClock} />
            <span>Mon - Sat: 8:00 AM - 6:00 PM</span>
          </div>
        </motion.div>

        {/* ============================================
            NEWSLETTER & PAYMENTS
            ============================================ */}
        <motion.div
          className="footer-section newsletter-section"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <h4>Stay Updated</h4>
          <p className="newsletter-text">
            Subscribe to receive updates on new products and special offers.
          </p>
          <form
            className="newsletter-form"
            onSubmit={(e) => e.preventDefault()}
          >
            <input
              type="email"
              placeholder="Enter your email"
              className="newsletter-input"
              aria-label="Email for newsletter"
            />
            <motion.button
              type="submit"
              className="newsletter-btn"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Subscribe
            </motion.button>
          </form>

          {/* Payment Methods */}
          <div className="payment-methods">
            <h5>We Accept</h5>
            <div className="payment-icons">
              {PAYMENT_METHODS.map((method, index) => (
                <span key={index} className="payment-icon" title={method.label}>
                  {method.icon} {method.label}
                </span>
              ))}
            </div>
          </div>

          {/* NRA Registration */}
          <div className="footer-nra">
            <FontAwesomeIcon icon={faCertificate} />
            <span>NRA Registration: 1001577205</span>
          </div>
        </motion.div>
      </div>

      {/* ============================================
          FOOTER BOTTOM
          ============================================ */}
      <div className="footer-bottom">
        <p>
          &copy; {currentYear} Kenali Enterprise. All rights reserved.
        </p>
        <div className="footer-bottom-links">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/shipping">Shipping Info</Link>
        </div>
        <p className="footer-tagline-bottom">🌾 Nutrition • Growth • Prosperity</p>
      </div>
    </footer>
  );
};

export default Footer;